import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { createStore, setStore, getPlans } from "@/lib/api";
import { PlanCard, PLAN_FALLBACK } from "@/components/chalann/PlanCard";
import { toast, Toaster } from "sonner";
import { Store as StoreIcon, ArrowRight, UserCheck } from "lucide-react";

const GIROS = [
  { key: "abarrotes", label: "Abarrotes / Miscelánea" },
  { key: "papeleria", label: "Papelería" },
  { key: "refaccionaria", label: "Refaccionaria" },
  { key: "boutique", label: "Boutique / Ropa" },
  { key: "fonda", label: "Fonda / Comida" },
  { key: "cerveceria", label: "Cervecería / Depósito" },
  { key: "farmacia", label: "Farmacia" },
  { key: "tortilleria", label: "Tortillería" },
  { key: "otro", label: "Otro giro" },
];

export default function Setup() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [giro, setGiro] = useState("abarrotes");
  const [owner, setOwner] = useState("");
  const [advisor, setAdvisor] = useState("");
  const [advisorPrefilled, setAdvisorPrefilled] = useState(false);
  const [plan, setPlan] = useState("chalanito");
  const [plans, setPlans] = useState(PLAN_FALLBACK);

  useEffect(() => { getPlans().then((r) => setPlans(r.plans)).catch(() => {}); }, []);

  useEffect(() => {
    const raw = params.get("advisor") || params.get("asesor");
    if (raw) {
      const clean = decodeURIComponent(raw).trim().slice(0, 80);
      if (clean) { setAdvisor(clean); setAdvisorPrefilled(true); }
    }
  }, [params]);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Ponle un nombre a tu tienda");
    setLoading(true);
    try {
      const store = await createStore({
        name: name.trim(),
        giro,
        owner_name: owner || null,
        commercial_advisor: advisor.trim() || null,
        plan,
      });
      setStore(store);
      toast.success(`¡Bienvenido a CHALANN, ${owner || "amigo"}! Activa tu plan ${plan === "onepay" ? "ONE PAY" : "CHALANITO"} para empezar a cobrar.`);
      nav("/pagar");
    } catch (err) {
      toast.error("No pudimos crear tu tienda. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6 relative overflow-hidden" data-testid="setup">
      <div className="grain-overlay" />
      <div className="glow-orange" style={{ top: "-30%", left: "-20%" }} />
      <div className="glow-lime" style={{ bottom: "-30%", right: "-20%" }} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-2xl border border-white/10 rounded-3xl bg-white/[0.02] backdrop-blur-xl p-8 md:p-12"
      >
        <div className="chip mb-6"><StoreIcon size={12} /> Alta de tienda</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95]">
          Dinos <span className="text-[#ff5a00]">cómo se llama</span><br /> tu changarro.
        </h1>
        <p className="text-white/60 mt-3 text-sm max-w-md">
          Cada tienda tiene su propio inventario, ventas y corte de caja. Puedes crear más adelante todos los comercios que quieras.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Nombre de la tienda</label>
            <input
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Miscelánea Doña Maru"
              className="w-full mt-2 bg-black/50 border border-white/10 rounded-xl px-4 py-3 font-body text-white placeholder-white/30 focus:outline-none focus:border-[#ff5a00] transition-colors"
              data-testid="setup-name"
            />
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Giro del negocio</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {GIROS.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setGiro(g.key)}
                  data-testid={`setup-giro-${g.key}`}
                  className={`px-4 py-2 rounded-full text-sm border transition-colors duration-200 ${
                    giro === g.key
                      ? "bg-[#ccff00] text-black border-[#ccff00]"
                      : "border-white/10 text-white/70 hover:border-white/40 hover:text-white"
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Tu nombre (opcional)</label>
            <input
              type="text"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              placeholder="Ej. Maru"
              className="w-full mt-2 bg-black/50 border border-white/10 rounded-xl px-4 py-3 font-body text-white placeholder-white/30 focus:outline-none focus:border-[#ff5a00] transition-colors"
              data-testid="setup-owner"
            />
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase flex items-center gap-2">
              Escribe el nombre de tu asesor comercial <span className="text-white/30 lowercase tracking-normal">(opcional)</span>
              {advisorPrefilled && (
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#ccff00]/10 border border-[#ccff00]/40 text-[#ccff00] px-2 py-0.5 text-[9px] tracking-widest">
                  <UserCheck size={9} /> por invitación
                </span>
              )}
            </label>
            <input
              type="text"
              value={advisor}
              onChange={(e) => { setAdvisor(e.target.value); setAdvisorPrefilled(false); }}
              placeholder="Ej. Roberto Sánchez"
              className={`w-full mt-2 bg-black/50 border rounded-xl px-4 py-3 font-body text-white placeholder-white/30 focus:outline-none transition-colors ${
                advisorPrefilled ? "border-[#ccff00]/50 bg-[#ccff00]/[0.03]" : "border-white/10 focus:border-[#ccff00]"
              }`}
              data-testid="setup-advisor"
            />
            <p className="text-white/40 text-[11px] mt-1">
              {advisorPrefilled
                ? `Te invitó ${advisor}. Puedes cambiarlo si lo necesitas.`
                : "Si algún vendedor de Chalann te contactó, escribe su nombre aquí."}
            </p>
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Elige tu plan</label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="setup-plans">
              {plans.map((p) => <PlanCard key={p.key} plan={p} compact selected={plan === p.key} onSelect={setPlan} />)}
            </div>
            <p className="text-white/40 text-[11px] mt-2">
              {plan === "onepay"
                ? "ONE PAY: pagas una vez al año y operas con pistola de escaneo, buscador y cámara. Sin comandos de voz ni IA."
                : "CHALANITO: Chalán entiende tus comandos de voz con IA. Pago mensual."}
              {" "}Sin periodo de prueba · puedes cambiar de plan después desde Pagar servicio.
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full justify-center text-base disabled:opacity-60"
            data-testid="setup-submit"
          >
            {loading ? "Creando..." : "Crear tienda y elegir cómo pagar"} <ArrowRight size={16} />
          </button>
        </form>
      </motion.div>
      <Toaster theme="dark" position="top-center" />
    </main>
  );
}
